import { BodySecondary, Caption } from '@liveauctioneers/hammer-ui-core/text';
import { memo } from 'react';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryCurrency,
    getItemSummaryHighBidEstimate,
    getItemSummaryLowBidEstimate,
    getItemSummarySellerId,
    getItemSummarySellerName,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getSellerUrl } from '@/utils/urls';

import { ItemCardLiveButton } from './components/ItemCardLiveButton';

type DetailsRowProps = {
    allowBidding?: boolean;
    hideLiveButton?: boolean;
    hideSeller?: boolean;
    isCatalogPageCard?: boolean;
    itemId: number;
    showDate?: boolean;
    showEstimate?: boolean;
};

const linkContainerClass = 'flex flex-[1_1_60%] mr-xs max-w-[60%] smMin:mr-sm max-[440px]:max-w-full';

const DetailsRowComponent = ({
    allowBidding,
    hideLiveButton,
    hideSeller,
    isCatalogPageCard,
    itemId,
    showDate,
    showEstimate,
}: DetailsRowProps) => {
    const formatMessage = useAppFormatMessage();
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const highBidEstimate = useAppSelector((state) => getItemSummaryHighBidEstimate(state, itemId));
    const lowBidEstimate = useAppSelector((state) => getItemSummaryLowBidEstimate(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemSummarySellerName(state, itemId));

    const estimateRange = {
        high: (
            <FormattedCurrency
                key="details-row-high-estimate"
                noFraction
                sourceCurrency={currency}
                value={highBidEstimate}
            />
        ),
        low: (
            <FormattedCurrency
                key="details-row-low-estimate"
                noFraction
                sourceCurrency={currency}
                value={lowBidEstimate}
            />
        ),
    };

    const shouldShowLiveButton = !hideLiveButton && !isCatalogPageCard;

    return (
        <div className="max-[440px]:flex-col flex flex-row justify-between">
            {showEstimate && (
                <BodySecondary className={linkContainerClass}>
                    <span className="inline grow-[3] overflow-visible whitespace-nowrap text-text-secondary [&>div]:mx-2xs">
                        {formatMessage(['short_estimate_values'], { values: estimateRange })}
                    </span>
                </BodySecondary>
            )}
            {!hideSeller && (
                <Caption className={linkContainerClass}>
                    <span className="flex grow-[3] overflow-hidden whitespace-nowrap">
                        {sellerName ? (
                            <RouteLink
                                className="max-w-full grow-[3]"
                                to={getSellerUrl(sellerId, sellerName)}
                            >
                                {sellerName}
                            </RouteLink>
                        ) : (
                            <span className="block w-full max-w-full grow-[3] overflow-hidden text-ellipsis whitespace-nowrap">
                                Jasper52
                            </span>
                        )}
                    </span>
                </Caption>
            )}
            {shouldShowLiveButton && (
                <ItemCardLiveButton
                    allowBidding={allowBidding}
                    itemId={itemId}
                    showDate={showDate}
                />
            )}
        </div>
    );
};

export const DetailsRow = memo<DetailsRowProps>(DetailsRowComponent);
